import * as React from "react";
import { connect } from "react-redux";
import { push } from "connected-react-router";
import { Route } from "react-router";
import { Box, Button, Menu, Divider, IconButton } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClearIcon from "@mui/icons-material/Clear";
import { tl, t } from "../../components/translate";
import styles from "./Lab.module.css";
import LabTestCreate from "./LabTestCreate/LabTestCreate";
import ClientSearch from "../../components/ClientSearch";
import { clientSearchSecondaryLabel } from "../../models/Client";

import DebouncedTextField from "../../components/DebouncedTextField";
import MedicalLabList from "./MedicalLabList";
import MedicalDetails from "./MedicalDetails";
import EnterTestResult from "./EnterTestResult";
import { MedicalTestRecordParams } from "../../api/medicalTest";

interface LabInterface {
  navigateTo: (path) => void;
}

interface SearchBoxProps {
  onSearch: (v: MedicalTestRecordParams) => void;
  searchParams: MedicalTestRecordParams;
}

export const SearchBox = ({ onSearch, searchParams }: SearchBoxProps): React.JSX.Element => {
  const [testId, setTestId] = React.useState("");
  const [client, setClient] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const openSearchMenu = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const closeSearchMenu = () => {
    setAnchorEl(null);
  };

  const searchComponentMapper = {
    LABTEST: {
      label: tl("lab.testId"),
      component: (
        <Box ml="3" display="flex">
          <DebouncedTextField
            value={testId}
            placeholder="Search ..."
            slotProps={{
              htmlInput: { style: { marginLeft: "35px", marginTop: "4px" } },
              input: { disableUnderline: true }
            }}
            onChange={(e) => {
              setTestId(e.target.value);
              onSearch({ ...searchParams, testId: e.target.value, clientId: "" });
            }}
            debounceAt={700}
          />
          {testId && (
            <IconButton
              aria-label="clear"
              size="small"
              onClick={() => {
                setTestId("");
                onSearch({ ...searchParams, testId: "", clientId: "" });
              }}
              style={{ padding: 0 }}
            >
              <ClearIcon />
            </IconButton>
          )}
        </Box>
      )
    },
    CLIENT: {
      label: tl("lab.client"),
      component: (
        <ClientSearch
          style={{
            width: "200px",
            marginTop: "5px",
            paddingLeft: "35px"
          }}
          client={client}
          setClient={(v) => {
            setClient(v);
            onSearch({ ...searchParams, testId: "", clientId: v?.id || "" });
          }}
          margin="none"
          placeholder="Search..."
          secondaryText={[
            {
              type: clientSearchSecondaryLabel.CUSTOMER_NUMBER
            },
            {
              type: clientSearchSecondaryLabel.EXTERNAL_IDENTIFIER,
              label: t("client.externalIdentifier.short")
            }
          ]}
        />
      )
    }
  };
  const menuItems = Object.keys(searchComponentMapper);
  const [active, setActive] = React.useState(menuItems[0]);

  const toggleSearch = (
    <Box>
      <Button
        style={{ width: "80px" }}
        onClick={(event) => {
          openSearchMenu(event);
        }}
      >
        {searchComponentMapper[active].label} <ExpandMoreIcon />
      </Button>
      <Menu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={closeSearchMenu}>
        {menuItems.map((item) => (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setActive(item);
              if (testId || client?.id) {
                onSearch({ ...searchParams, testId: "", clientId: "" });
              }
              if (item === "CLIENT") {
                setTestId("");
              } else if (item === "LABTEST") {
                setClient(null);
              }
            }}
            key={item}
          >
            {searchComponentMapper[item].label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );

  return (
    <Box
      sx={(theme) => ({
        width: "300px",
        display: "flex",
        position: "relative",
        alignItems: "center",
        height: "35px",
        borderRadius: "5px",
        transition: "width .5s,background 1s ease",
        backgroundColor: "#ececec",
        [theme.breakpoints.down("sm")]: {
          width: "240px"
        }
      })}
    >
      <SearchIcon
        sx={{
          position: "absolute",
          width: "40px"
        }}
      />
      <Box>{searchComponentMapper[active].component}</Box>
      <Divider style={{ height: 20, margin: 4 }} orientation="vertical" />
      <Box>{toggleSearch}</Box>
    </Box>
  );
};

const Lab: React.FC<LabInterface> = ({ navigateTo }) => (
  <Box className={styles.labRoot}>
    <Box>
      <Box>
        <MedicalLabList />
      </Box>
      <Route exact path="/medical/create" render={() => <LabTestCreate />} />
      <Route
        exact
        path="/medical/details/:id"
        render={(props) => (
          <MedicalDetails handleClose={() => navigateTo("/medical")} id={props.match.params.id} />
        )}
      />
      <Route
        exact
        path="/medical/entry/:id"
        render={(props) => (
          <EnterTestResult handleClose={() => navigateTo("/medical")} id={props.match.params.id} />
        )}
      />
    </Box>
  </Box>
);

const mapDispatchToProps = (dispatch) => ({
  navigateTo: (path) => {
    dispatch(push(path));
  }
});

export default connect(null, mapDispatchToProps)(Lab);
